// Prefetch utilities for critical dashboard data
import { QueryClient } from '@tanstack/react-query';
import { adminApiService } from '@/services/adminApi';
import { dashboardQueryKeys } from '@/hooks/useDashboardMetrics';
import { createLogger } from './logger';

const logger = createLogger('Prefetch');

/**
 * Prefetch critical dashboard data on app initialization
 * This improves perceived performance by loading data before the user navigates to the dashboard
 */
export const prefetchDashboardData = async (queryClient: QueryClient) => {
  try {
    logger.info('Starting dashboard data prefetch...');
    
    // Prefetch dashboard metrics (most critical data)
    await queryClient.prefetchQuery({
      queryKey: dashboardQueryKeys.overview(),
      queryFn: async () => {
        const response = await adminApiService.getDashboardMetrics();
        return response.data;
      },
      staleTime: 3 * 60 * 1000, // 3 minutes
    });
    
    logger.info('Dashboard data prefetch completed successfully');
  } catch (error) {
    // Don't throw - prefetch failures should not block app initialization
    logger.warn('Dashboard data prefetch failed (non-critical):', error);
  }
};

/**
 * Prefetch user count data for faster initial load
 */
export const prefetchUserCount = async (queryClient: QueryClient) => {
  try {
    logger.info('Starting user count prefetch...');
    
    await queryClient.prefetchQuery({
      queryKey: ['unified-user-count'],
      queryFn: async () => {
        const dashboardResponse = await adminApiService.getDashboardMetrics();
        const overviewTotal = dashboardResponse.data?.overview?.total_users || 0;
        const tierData = dashboardResponse.data?.usage?.by_tier?.reduce((acc, tierInfo) => {
          acc[tierInfo.tier] = tierInfo.user_count;
          return acc;
        }, {} as Record<string, number>) || null;
        
        return {
          totalUsers: overviewTotal,
          dashboardTotal: overviewTotal,
          usersTotal: overviewTotal,
          tierCounts: tierData,
          source: 'analytics_overview',
          lastUpdated: new Date().toISOString(),
        };
      },
      staleTime: 3 * 60 * 1000,
    });
    
    logger.info('User count prefetch completed successfully');
  } catch (error) {
    logger.warn('User count prefetch failed (non-critical):', error);
  }
};

/**
 * Prefetch all critical data in parallel
 */
export const prefetchCriticalData = async (queryClient: QueryClient) => {
  const startTime = Date.now();
  logger.info('Starting critical data prefetch...');
  
  try {
    // Run all prefetch operations in parallel for maximum speed
    await Promise.allSettled([
      prefetchDashboardData(queryClient),
      prefetchUserCount(queryClient),
    ]);
    
    const duration = Date.now() - startTime;
    logger.info(`Critical data prefetch completed in ${duration}ms`);
  } catch (error) {
    logger.error('Critical data prefetch failed:', error);
  }
};

// Track if prefetch has been initialized to prevent duplicates
let prefetchInitialized = false;

/**
 * Initialize prefetching after authentication
 */
export const initializePrefetch = (queryClient: QueryClient) => {
  // Prevent duplicate initialization
  if (prefetchInitialized) {
    logger.debug('Prefetch already initialized, skipping duplicate call');
    return;
  }
  
  // Check if user is authenticated before prefetching
  const token = localStorage.getItem('admin_session_token') || localStorage.getItem('admin_token');
  
  if (token) {
    prefetchInitialized = true;
    
    // Delay prefetch slightly to not block initial render
    setTimeout(() => {
      prefetchCriticalData(queryClient);
    }, 100);
  }
};

